import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ScanFace } from 'lucide-react';

interface BiometricConsentGateProps {
  onConsentGranted: () => void;
  onCancel: () => void;
}

export function BiometricConsentGate({ onConsentGranted, onCancel }: BiometricConsentGateProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleGrant = async () => {
    if (!agreed || !user) return;

    setIsSubmitting(true);
    try {
      const now = new Date().toISOString();

      await supabase.from('consent_records').insert({
        user_id: user.id,
        consent_type: 'biometric_video_processing',
        consent_version: '2026-03-01',
        granted_at: now,
      } as any);

      await supabase
        .from('profiles')
        .update({ biometric_consent_at: now } as any)
        .eq('user_id', user.id);

      toast({
        title: 'Consent Recorded',
        description: 'You can now upload videos for analysis.',
      });
      onConsentGranted();
    } catch {
      toast({
        title: 'Error',
        description: 'Could not save consent. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-6 space-y-5">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <ScanFace className="w-6 h-6 text-blue-700" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-1">Video Analysis Consent</h2>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            <p>
              No Doubles analyzes your uploaded videos to detect body position and movement
              patterns. Depending on your location, this may involve processing biometric data.
            </p>
            <p>
              By continuing, you consent to the collection and processing of movement data derived
              from your video uploads for the purpose of providing swing analysis and coaching
              feedback.
            </p>
            <p>
              You may withdraw this consent at any time in your account settings. Withdrawing
              consent will prevent you from uploading new videos for analysis.
            </p>
          </div>

          <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
            <Checkbox
              checked={agreed}
              onCheckedChange={(checked) => setAgreed(checked === true)}
              disabled={isSubmitting}
              className="mt-0.5 flex-shrink-0"
            />
            <span className="text-sm text-gray-700">
              I understand and consent to video analysis processing
            </span>
          </div>

          <div className="flex gap-2 text-xs text-gray-500">
            <Link
              to="/terms/ai-disclosure"
              target="_blank"
              className="text-emerald-700 hover:underline"
            >
              AI Disclosure Addendum
            </Link>
            <span>•</span>
            <Link
              to="/privacy#3-biometric-data"
              target="_blank"
              className="text-emerald-700 hover:underline"
            >
              Privacy Policy — Biometric Data
            </Link>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1 h-11"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGrant}
              disabled={!agreed || isSubmitting}
              className="flex-1 h-11 bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Continue'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
